import { addDays, startOfIsoWeek, todayIn, type DateOnly } from '@/domain/dates'
import {
  buildCalendar,
  can,
  computeBalance,
  DEFAULT_SETTINGS,
  supervisedIds,
  validateRequest,
  workingDaysBetween,
  type ActivityItem,
  type BalanceRecord,
  type ChangeRequest,
  type CustomDay,
  type Department,
  type Employee,
  type LeaveRequest,
  type Membership,
  type Organization,
  type Role,
} from '@/domain/vacations'
import { AppError } from '@/lib/errors'
import type { AuthUser } from './auth'
import type { VacationsApi, Workspace } from './api'

/**
 * Browser-only implementation of the API, persisted in localStorage.
 * It enforces the same rules the real API/RLS will (permissions, overlaps, balance,
 * team limit), so the UI behaves exactly as it will in production.
 */

type WithOrg<T> = T & { orgId: string }

interface MockDb {
  version: 2
  orgs: Organization[]
  members: Array<{ orgId: string; email: string; role: Role; employeeId: string | null }>
  employees: WithOrg<Employee>[]
  departments: WithOrg<Department>[]
  requests: WithOrg<LeaveRequest>[]
  changes: WithOrg<ChangeRequest>[]
  balances: WithOrg<BalanceRecord>[]
  customDays: WithOrg<CustomDay>[]
  activity: WithOrg<ActivityItem>[]
}

const KEY = 'nolens-vacations.mock-db'
const uid = () => crypto.randomUUID()
const nowIso = () => new Date().toISOString()
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

// ---------------------------------------------------------------------------
// Seed: a fictitious organization whose dates are relative to today, so the demo
// always shows something happening (people away today, pending approvals…).
// ---------------------------------------------------------------------------

function seed(): MockDb {
  const today = todayIn()
  const year = Number(today.slice(0, 4))
  const cal = buildCalendar()
  const orgId = uid()
  const org: Organization = { id: orgId, name: 'Atlântico Viagens', settings: { ...DEFAULT_SETTINGS }, createdAt: nowIso() }

  const dept = (name: string, maxConcurrent: number | null): WithOrg<Department> => ({ id: uid(), orgId, name, managerId: null, maxConcurrent })
  const loja = dept('Loja Lisboa', 2)
  const ops = dept('Operações', 3)
  const mkt = dept('Marketing', 2)

  const person = (name: string, email: string, role: Role, d: Department | null, managerId: string | null, birth: string, hire: string): WithOrg<Employee> => ({
    id: uid(),
    orgId,
    name,
    email,
    role,
    departmentId: d?.id ?? null,
    managerId,
    annualDays: null,
    birthDate: birth as DateOnly,
    hireDate: hire as DateOnly,
    active: true,
  })
  const sofia = person('Sofia Andrade', 'admin@demo.nolens.app', 'admin', ops, null, '1982-03-15', '2014-01-06')
  const rui = person('Rui Matos', 'rui@demo.nolens.app', 'manager', loja, sofia.id, '1986-07-22', '2017-06-01')
  const marta = person('Marta Lopes', 'marta@demo.nolens.app', 'hr', ops, sofia.id, '1989-11-02', '2019-02-11')
  const ana = person('Ana Ribeiro', 'ana@demo.nolens.app', 'employee', loja, rui.id, `1994-${today.slice(5, 7)}-28`, '2021-03-15')
  const tiago = person('Tiago Ferreira', 'tiago@demo.nolens.app', 'employee', loja, rui.id, '1991-05-30', '2020-09-01')
  const ines = person('Inês Carvalho', 'ines@demo.nolens.app', 'employee', loja, rui.id, '1997-01-19', '2023-04-03')
  const joana = person('Joana Costa', 'joana@demo.nolens.app', 'employee', mkt, rui.id, '1990-08-08', '2018-10-15')
  const miguel = person('Miguel Sousa', 'miguel@demo.nolens.app', 'employee', mkt, rui.id, '1993-12-12', '2022-01-10')
  const pedro = person('Pedro Santos', 'pedro@demo.nolens.app', 'employee', ops, sofia.id, '1988-04-04', '2016-05-02')
  const beatriz = person('Beatriz Almeida', 'beatriz@demo.nolens.app', 'employee', ops, sofia.id, '1995-09-27', '2024-02-05')
  loja.managerId = rui.id
  mkt.managerId = rui.id
  ops.managerId = sofia.id
  const employees = [sofia, rui, marta, ana, tiago, ines, joana, miguel, pedro, beatriz]

  const monday = (offsetDays: number) => cal.nextBusinessDay(startOfIsoWeek(addDays(today, offsetDays)))
  const request = (e: Employee, start: DateOnly, workingDays: number, status: LeaveRequest['status'], extra: Partial<LeaveRequest> = {}): WithOrg<LeaveRequest> => {
    const s = cal.nextBusinessDay(start)
    const end = workingDays > 1 ? cal.addBusinessDays(s, workingDays - 1) : s
    const decided = status === 'approved' || status === 'rejected'
    return {
      id: uid(),
      orgId,
      employeeId: e.id,
      type: 'vacation',
      start: s,
      end,
      workingDays: workingDaysBetween(cal, s, end),
      status,
      comment: null,
      createdAt: new Date(Date.now() - 20 * 86_400_000).toISOString(),
      createdBy: e.email,
      decidedBy: decided ? (e.managerId ? employees.find((x) => x.id === e.managerId)!.email : sofia.email) : null,
      decidedAt: decided ? new Date(Date.now() - 18 * 86_400_000).toISOString() : null,
      decisionComment: null,
      ...extra,
    }
  }

  const joanaNov = request(joana, monday(35), 5, 'approved')
  const miguelDec = request(miguel, monday(49), 4, 'approved')
  const requests = [
    // Ana: 8 days taken, longest period 5 → the 10-day rule is still open
    request(ana, monday(-200), 3, 'approved'),
    request(ana, monday(-70), 5, 'approved'),
    // Rui
    request(rui, monday(-120), 10, 'approved'),
    request(rui, monday(56), 3, 'pending'),
    // People away today
    request(pedro, monday(-4), 10, 'approved'),
    request(beatriz, addDays(today, -1), 3, 'approved'),
    // Pending approvals for Rui (with a team-limit conflict at the end)
    request(tiago, monday(28), 5, 'pending', { comment: 'Viagem de família já marcada.' }),
    request(ines, monday(63), 8, 'pending'),
    request(tiago, monday(63), 4, 'approved'),
    joanaNov,
    miguelDec,
    request(joana, monday(-150), 10, 'approved'),
    request(miguel, monday(-90), 10, 'approved'),
    request(tiago, monday(-110), 10, 'approved'),
    request(ines, monday(-60), 6, 'approved'),
    request(marta, monday(-130), 10, 'approved'),
    request(marta, monday(21), 3, 'approved'),
    request(sofia, monday(-100), 12, 'approved'),
    request(beatriz, monday(-80), 5, 'rejected', { decisionComment: 'Coincide com a auditoria anual.' }),
  ]
  const changes: WithOrg<ChangeRequest>[] = [
    {
      id: uid(),
      orgId,
      requestId: joanaNov.id,
      employeeId: joana.id,
      kind: 'modify',
      newStart: addDays(joanaNov.start, 7),
      newEnd: addDays(joanaNov.end, 7),
      newWorkingDays: workingDaysBetween(cal, addDays(joanaNov.start, 7), addDays(joanaNov.end, 7)),
      reason: 'O voo foi adiado uma semana.',
      status: 'pending',
      createdAt: new Date(Date.now() - 2 * 86_400_000).toISOString(),
      decidedBy: null,
      decidedAt: null,
      decisionComment: null,
    },
    {
      id: uid(),
      orgId,
      requestId: miguelDec.id,
      employeeId: miguel.id,
      kind: 'cancel',
      newStart: null,
      newEnd: null,
      newWorkingDays: null,
      reason: 'Prefiro guardar os dias para o Natal.',
      status: 'pending',
      createdAt: new Date(Date.now() - 86_400_000).toISOString(),
      decidedBy: null,
      decidedAt: null,
      decisionComment: null,
    },
  ]
  const balances: WithOrg<BalanceRecord>[] = [
    { orgId, employeeId: ana.id, year, contractDays: null, carriedOver: 3, notes: null },
    { orgId, employeeId: tiago.id, year, contractDays: null, carriedOver: 2, notes: null },
    { orgId, employeeId: rui.id, year, contractDays: 25, carriedOver: 0, notes: 'Contrato com 25 dias.' },
    { orgId, employeeId: ana.id, year: year + 1, contractDays: null, carriedOver: 0, notes: null },
  ]
  const customDays: WithOrg<CustomDay>[] = [
    { id: uid(), orgId, date: `${year}-06-13` as DateOnly, name: 'Santo António (Lisboa)', kind: 'holiday' },
    { id: uid(), orgId, date: `${year}-12-24` as DateOnly, name: 'Véspera de Natal', kind: 'bridge' },
    { id: uid(), orgId, date: `${year}-12-31` as DateOnly, name: 'Véspera de Ano Novo', kind: 'bridge' },
    { id: uid(), orgId, date: `${year + 1}-06-13` as DateOnly, name: 'Santo António (Lisboa)', kind: 'holiday' },
  ]
  const members = employees.map((e) => ({ orgId, email: e.email, role: e.role, employeeId: e.id }))
  const activity: WithOrg<ActivityItem>[] = [
    { id: uid(), orgId, at: new Date(Date.now() - 86_400_000).toISOString(), actorName: 'Miguel Sousa', text: 'pediu para cancelar férias aprovadas' },
    { id: uid(), orgId, at: new Date(Date.now() - 2 * 86_400_000).toISOString(), actorName: 'Joana Costa', text: 'pediu para alterar o período de férias' },
    { id: uid(), orgId, at: new Date(Date.now() - 3 * 86_400_000).toISOString(), actorName: 'Tiago Ferreira', text: 'marcou 5 dias de férias' },
  ]
  return { version: 2, orgs: [org], members, employees, departments: [loja, ops, mkt], requests, changes, balances, customDays, activity }
}

// ---------------------------------------------------------------------------

function load(): MockDb {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) {
      const db = JSON.parse(raw) as MockDb
      if (db.version === 2) return db
    }
  } catch {
    // corrupted or unavailable storage → fresh seed
  }
  return seed()
}

export function resetMockData() {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // ignore
  }
}

const strip = <T extends { orgId: string }>({ orgId: _o, ...rest }: T) => rest

export function createMockApi(currentUser: () => AuthUser | null): VacationsApi {
  let db = load()
  const save = () => {
    try {
      localStorage.setItem(KEY, JSON.stringify(db))
    } catch {
      // storage full / private mode: keep in memory
    }
  }
  save()

  const me = () => {
    const u = currentUser()
    if (!u) throw new AppError('A sua sessão expirou. Inicie sessão novamente.', 'auth')
    return u
  }
  const membership = (orgId: string) => {
    const u = me()
    const m = db.members.find((x) => x.orgId === orgId && x.email === u.email.toLowerCase())
    if (!m) throw new AppError('Não tem acesso a esta organização.', 'permission')
    return { ...m, user: u }
  }
  const org = (orgId: string) => db.orgs.find((o) => o.id === orgId)!
  const actorName = (orgId: string, email: string) => db.employees.find((e) => e.orgId === orgId && e.email === email)?.name ?? email
  const log = (orgId: string, email: string, text: string) => {
    db.activity.unshift({ id: uid(), orgId, at: nowIso(), actorName: actorName(orgId, email), text })
    db.activity = db.activity.slice(0, 200)
  }
  const orgEmployees = (orgId: string) => db.employees.filter((e) => e.orgId === orgId).map(strip)
  const orgRequests = (orgId: string) => db.requests.filter((r) => r.orgId === orgId).map(strip)
  const calendarOf = (orgId: string) => buildCalendar(db.customDays.filter((d) => d.orgId === orgId).map(strip))
  const mayDecideFor = (orgId: string, role: Role, myEmployeeId: string | null, employeeId: string) => {
    if (role === 'admin') return true
    if (role !== 'manager') return false
    if (employeeId === myEmployeeId) return false
    return db.employees.find((e) => e.orgId === orgId && e.id === employeeId)?.managerId === myEmployeeId
  }
  const toMembership = (m: MockDb['members'][number]): Membership => ({ organization: org(m.orgId), role: m.role, employeeId: m.employeeId })

  const api: VacationsApi = {
    async memberships() {
      await sleep(120)
      const u = me()
      return db.members.filter((m) => m.email === u.email.toLowerCase()).map(toMembership)
    },

    async createOrganization(input) {
      await sleep(250)
      const u = me()
      const orgId = uid()
      const organization: Organization = { id: orgId, name: input.name.trim(), settings: input.settings, createdAt: nowIso() }
      const departments = input.departments.filter((n) => n.trim()).map((name) => ({ id: uid(), orgId, name: name.trim(), managerId: null, maxConcurrent: null }))
      const employee: WithOrg<Employee> = {
        id: uid(),
        orgId,
        name: input.ownerName.trim() || u.name,
        email: u.email.toLowerCase(),
        role: 'admin',
        departmentId: departments[0]?.id ?? null,
        managerId: null,
        annualDays: null,
        birthDate: null,
        hireDate: null,
        active: true,
      }
      db.orgs.push(organization)
      db.departments.push(...departments)
      db.employees.push(employee)
      const m = { orgId, email: employee.email, role: 'admin' as Role, employeeId: employee.id }
      db.members.push(m)
      log(orgId, employee.email, `criou a organização ${organization.name}`)
      save()
      return toMembership(m)
    },

    async workspace(orgId) {
      await sleep(180)
      const m = membership(orgId)
      const employees = orgEmployees(orgId)
      const supervised = supervisedIds(m.role, m.employeeId, employees)
      const seesAll = can(m.role, 'people.manage')
      const visible = (id: string) => id === m.employeeId || supervised.has(id)
      const requests = orgRequests(orgId)
        .filter((r) => visible(r.employeeId) || r.status === 'approved' || r.status === 'pending')
        .map((r) => (visible(r.employeeId) ? r : { ...r, comment: null, decisionComment: null }))
      const ws: Workspace = {
        organization: org(orgId),
        role: m.role,
        me: employees.find((e) => e.id === m.employeeId) ?? null,
        employees: seesAll ? employees : employees.filter((e) => e.active),
        departments: db.departments.filter((d) => d.orgId === orgId).map(strip),
        requests,
        changeRequests: db.changes.filter((c) => c.orgId === orgId && visible(c.employeeId)).map(strip),
        balanceRecords: db.balances.filter((b) => b.orgId === orgId && visible(b.employeeId)).map(strip),
        customDays: db.customDays.filter((d) => d.orgId === orgId).map(strip),
      }
      return structuredClone(ws)
    },

    async activity(orgId) {
      await sleep(100)
      membership(orgId)
      return db.activity.filter((a) => a.orgId === orgId).slice(0, 20).map(strip)
    },

    async createRequest(orgId, input) {
      await sleep(300)
      const m = membership(orgId)
      const targetId = input.employeeId ?? m.employeeId
      if (!targetId) throw new AppError('Esta conta não tem ficha de colaborador. Use "Marcar por outra pessoa".', 'validation')
      if (targetId !== m.employeeId && !can(m.role, 'requests.book_for_others')) {
        throw new AppError('Sem permissão para marcar férias por outra pessoa.', 'permission')
      }
      const employees = orgEmployees(orgId)
      const employee = employees.find((e) => e.id === targetId && e.active)
      if (!employee) throw new AppError('Colaborador não encontrado ou inativo.', 'not_found')
      const o = org(orgId)
      const cal = calendarOf(orgId)
      const requests = orgRequests(orgId)
      const year = Number(input.start.slice(0, 4))
      const balance = computeBalance({
        employee,
        year,
        requests,
        record: db.balances.find((b) => b.orgId === orgId && b.employeeId === employee.id && b.year === year),
        settings: o.settings,
        calendar: cal,
        today: todayIn(o.settings.timezone),
      })
      const v = validateRequest({
        calendar: cal,
        today: todayIn(o.settings.timezone),
        type: input.type,
        start: input.start,
        end: input.end,
        employee,
        employees,
        department: db.departments.find((d) => d.id === employee.departmentId),
        requests,
        balance,
      })
      const error = v.issues.find((i) => i.level === 'error')
      if (error) throw new AppError(error.message, 'validation')
      const r: WithOrg<LeaveRequest> = {
        id: uid(),
        orgId,
        employeeId: employee.id,
        type: input.type,
        start: input.start,
        end: input.end,
        workingDays: v.workingDays,
        status: 'pending',
        comment: input.comment?.trim() || null,
        createdAt: nowIso(),
        createdBy: m.user.email,
        decidedBy: null,
        decidedAt: null,
        decisionComment: null,
      }
      db.requests.push(r)
      log(orgId, m.user.email, targetId === m.employeeId ? `marcou ${v.workingDays} ${v.workingDays === 1 ? 'dia' : 'dias'} de férias` : `marcou férias por ${employee.name}`)
      save()
      return strip(r)
    },

    async cancelRequest(orgId, requestId) {
      await sleep(200)
      const m = membership(orgId)
      const r = db.requests.find((x) => x.orgId === orgId && x.id === requestId)
      if (!r) throw new AppError('Pedido não encontrado.', 'not_found')
      if (r.status !== 'pending') throw new AppError('Só é possível cancelar pedidos pendentes. Para férias aprovadas, peça o cancelamento ao gestor.', 'validation')
      if (r.employeeId !== m.employeeId && !can(m.role, 'requests.book_for_others')) throw new AppError('Sem permissão para cancelar este pedido.', 'permission')
      r.status = 'cancelled'
      log(orgId, m.user.email, 'cancelou um pedido pendente')
      save()
    },

    async decideRequest(orgId, requestId, decision, comment) {
      await sleep(250)
      const m = membership(orgId)
      const r = db.requests.find((x) => x.orgId === orgId && x.id === requestId)
      if (!r) throw new AppError('Pedido não encontrado.', 'not_found')
      if (r.status !== 'pending') throw new AppError('Este pedido já foi decidido.', 'conflict')
      if (!mayDecideFor(orgId, m.role, m.employeeId, r.employeeId)) throw new AppError('Sem permissão para decidir este pedido.', 'permission')
      if (decision === 'reject' && !comment?.trim()) throw new AppError('Indique o motivo da recusa.', 'validation')
      r.status = decision === 'approve' ? 'approved' : 'rejected'
      r.decidedBy = m.user.email
      r.decidedAt = nowIso()
      r.decisionComment = comment?.trim() || null
      const who = db.employees.find((e) => e.id === r.employeeId)?.name ?? ''
      log(orgId, m.user.email, `${decision === 'approve' ? 'aprovou' : 'recusou'} as férias de ${who}`)
      save()
    },

    async createChange(orgId, input) {
      await sleep(250)
      const m = membership(orgId)
      const r = db.requests.find((x) => x.orgId === orgId && x.id === input.requestId)
      if (!r || r.employeeId !== m.employeeId) throw new AppError('Pedido não encontrado.', 'not_found')
      if (r.status !== 'approved') throw new AppError('Só férias aprovadas precisam de pedido de alteração.', 'validation')
      if (db.changes.some((c) => c.requestId === r.id && c.status === 'pending')) throw new AppError('Já existe um pedido de alteração pendente para estas férias.', 'conflict')
      if (!input.reason.trim()) throw new AppError('Indique o motivo.', 'validation')
      let newWorkingDays: number | null = null
      if (input.kind === 'modify') {
        if (!input.newStart || !input.newEnd) throw new AppError('Indique o novo período.', 'validation')
        const o = org(orgId)
        const cal = calendarOf(orgId)
        const employees = orgEmployees(orgId)
        const employee = employees.find((e) => e.id === r.employeeId)!
        const requests = orgRequests(orgId)
        const year = Number(input.newStart.slice(0, 4))
        const others = requests.filter((x) => x.id !== r.id)
        const balance = computeBalance({ employee, year, requests: others, record: db.balances.find((b) => b.orgId === orgId && b.employeeId === employee.id && b.year === year), settings: o.settings, calendar: cal, today: todayIn() })
        const v = validateRequest({ calendar: cal, today: todayIn(), type: r.type, start: input.newStart, end: input.newEnd, employee, employees, department: db.departments.find((d) => d.id === employee.departmentId), requests, balance, ignoreRequestId: r.id })
        const error = v.issues.find((i) => i.level === 'error')
        if (error) throw new AppError(error.message, 'validation')
        newWorkingDays = v.workingDays
      }
      const c: WithOrg<ChangeRequest> = {
        id: uid(),
        orgId,
        requestId: r.id,
        employeeId: r.employeeId,
        kind: input.kind,
        newStart: input.newStart ?? null,
        newEnd: input.newEnd ?? null,
        newWorkingDays,
        reason: input.reason.trim(),
        status: 'pending',
        createdAt: nowIso(),
        decidedBy: null,
        decidedAt: null,
        decisionComment: null,
      }
      db.changes.push(c)
      log(orgId, m.user.email, input.kind === 'modify' ? 'pediu para alterar o período de férias' : 'pediu para cancelar férias aprovadas')
      save()
      return strip(c)
    },

    async decideChange(orgId, changeId, decision, comment) {
      await sleep(250)
      const m = membership(orgId)
      const c = db.changes.find((x) => x.orgId === orgId && x.id === changeId)
      if (!c) throw new AppError('Pedido não encontrado.', 'not_found')
      if (c.status !== 'pending') throw new AppError('Este pedido já foi decidido.', 'conflict')
      if (!mayDecideFor(orgId, m.role, m.employeeId, c.employeeId)) throw new AppError('Sem permissão para decidir este pedido.', 'permission')
      c.status = decision === 'approve' ? 'approved' : 'rejected'
      c.decidedBy = m.user.email
      c.decidedAt = nowIso()
      c.decisionComment = comment?.trim() || null
      if (decision === 'approve') {
        const r = db.requests.find((x) => x.id === c.requestId)!
        if (c.kind === 'cancel') r.status = 'cancelled'
        else {
          r.start = c.newStart!
          r.end = c.newEnd!
          r.workingDays = c.newWorkingDays!
        }
      }
      log(orgId, m.user.email, `${decision === 'approve' ? 'aprovou' : 'recusou'} um pedido de ${c.kind === 'cancel' ? 'cancelamento' : 'alteração'}`)
      save()
    },

    async saveEmployee(orgId, input) {
      await sleep(250)
      const m = membership(orgId)
      if (!can(m.role, 'people.manage')) throw new AppError('Sem permissão para gerir pessoas.', 'permission')
      const email = input.email.trim().toLowerCase()
      if (db.employees.some((e) => e.orgId === orgId && e.email === email && e.id !== input.id)) throw new AppError('Já existe uma pessoa com este email.', 'conflict')
      if (input.role === 'admin' && m.role !== 'admin') throw new AppError('Só um administrador pode atribuir o papel de administrador.', 'permission')
      if (input.id && input.id === m.employeeId && (input.role !== m.role || !input.active)) throw new AppError('Não pode alterar o próprio papel nem desativar a própria conta.', 'permission')
      let e = db.employees.find((x) => x.orgId === orgId && x.id === input.id)
      if (e) {
        Object.assign(e, { ...input, email })
        const mem = db.members.find((x) => x.orgId === orgId && x.employeeId === e!.id)
        if (mem) Object.assign(mem, { email, role: input.role })
      } else {
        e = { ...input, id: uid(), orgId, email }
        db.employees.push(e)
        db.members.push({ orgId, email, role: input.role, employeeId: e.id })
      }
      log(orgId, m.user.email, `${input.id ? 'atualizou' : 'adicionou'} ${e.name}`)
      save()
      return strip(e)
    },

    async saveDepartment(orgId, input) {
      await sleep(200)
      const m = membership(orgId)
      if (!can(m.role, 'people.manage')) throw new AppError('Sem permissão para gerir departamentos.', 'permission')
      let d = db.departments.find((x) => x.orgId === orgId && x.id === input.id)
      if (d) Object.assign(d, input)
      else {
        d = { ...input, id: uid(), orgId }
        db.departments.push(d)
      }
      save()
      return strip(d)
    },

    async saveBalanceRecord(orgId, input) {
      await sleep(200)
      const m = membership(orgId)
      if (!can(m.role, 'people.manage')) throw new AppError('Sem permissão para editar saldos.', 'permission')
      const i = db.balances.findIndex((b) => b.orgId === orgId && b.employeeId === input.employeeId && b.year === input.year)
      if (i >= 0) db.balances[i] = { ...input, orgId }
      else db.balances.push({ ...input, orgId })
      log(orgId, m.user.email, `atualizou o saldo de ${db.employees.find((e) => e.id === input.employeeId)?.name ?? ''}`)
      save()
    },

    async saveCustomDay(orgId, input) {
      await sleep(150)
      const m = membership(orgId)
      if (!can(m.role, 'settings.manage')) throw new AppError('Sem permissão para gerir o calendário.', 'permission')
      if (db.customDays.some((d) => d.orgId === orgId && d.date === input.date && d.id !== input.id)) throw new AppError('Já existe um dia marcado nesta data.', 'conflict')
      let d = db.customDays.find((x) => x.orgId === orgId && x.id === input.id)
      if (d) Object.assign(d, input)
      else {
        d = { ...input, id: uid(), orgId }
        db.customDays.push(d)
      }
      save()
      return strip(d)
    },

    async deleteCustomDay(orgId, id) {
      await sleep(150)
      const m = membership(orgId)
      if (!can(m.role, 'settings.manage')) throw new AppError('Sem permissão para gerir o calendário.', 'permission')
      db.customDays = db.customDays.filter((d) => !(d.orgId === orgId && d.id === id))
      save()
    },

    async updateSettings(orgId, settings, name) {
      await sleep(200)
      const m = membership(orgId)
      if (!can(m.role, 'settings.manage')) throw new AppError('Sem permissão para alterar as regras.', 'permission')
      const o = org(orgId)
      o.settings = settings
      if (name?.trim()) o.name = name.trim()
      log(orgId, m.user.email, 'atualizou as regras de férias')
      save()
      return structuredClone(o)
    },
  }

  // Reload when another tab changes the demo data.
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) db = load()
  })
  return api
}
